import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";

// Turns shown before "View history" is expanded.
const DEFAULT_COUNT = 3;
// Upper bound loaded when expanded (reads are cheap — one small doc per turn).
const EXPANDED_LIMIT = 200;

const formatTurnDate = (dateStr) => {
  if (!dateStr) return "";
  // Parse as local midnight so the YYYY-MM-DD key doesn't shift a day.
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Grey chip = dispatched, green chip = dispatched + completed.
const EmployeeChip = ({ entry }) => (
  <Chip
    size="small"
    label={entry.name}
    color={entry.completed ? "success" : "default"}
    icon={entry.completed ? <CheckCircleIcon /> : undefined}
    sx={
      entry.completed
        ? {}
        : { bgcolor: "grey.300", color: "text.primary" }
    }
  />
);

const DepartmentRow = ({ label, entries }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap",
      mb: 0.5,
    }}
  >
    <Typography
      variant="body2"
      sx={{ minWidth: 110, color: "text.secondary", fontWeight: 500 }}
    >
      {label}
    </Typography>
    {entries.length === 0 ? (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    ) : (
      entries.map((e, i) => <EmployeeChip key={`${e.name}-${i}`} entry={e} />)
    )}
  </Box>
);

function RoomTurnHistory({ roomDisplayName }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!roomDisplayName || !selectedProperty || !selectedClient) return undefined;

    setLoading(true);
    const turnRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "units",
      roomDisplayName,
      "turnHistory"
    );
    const q = query(
      turnRef,
      orderBy("date", "desc"),
      limit(expanded ? EXPANDED_LIMIT : DEFAULT_COUNT)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            const employees = Object.values(data.employees || {}).filter(Boolean);
            return {
              date: data.date || doc.id,
              housekeeping: employees.filter((e) => e.type === "housekeeping"),
              maintenance: employees.filter((e) => e.type === "maintenance"),
              total: employees.length,
            };
          })
          // Entries can be emptied when all tasks are removed — skip those.
          .filter((t) => t.total > 0);
        setTurns(rows);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading turn history:", error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [roomDisplayName, selectedProperty, selectedClient, expanded]);

  // Only offer the toggle when there may be more than the default view shows.
  const showToggle = expanded || turns.length >= DEFAULT_COUNT;

  return (
    <Card sx={{ minWidth: 275, marginTop: "50px" }}>
      <CardHeader
        style={{ background: "#282828" }}
        titleTypographyProps={{ color: "white" }}
        title="Turn History"
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : turns.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No turn history yet for this room.
          </Typography>
        ) : (
          <>
            <Stack divider={<Divider flexItem />} spacing={1.5}>
              {turns.map((t) => (
                <Box key={t.date}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {formatTurnDate(t.date)}
                  </Typography>
                  <DepartmentRow label="Housekeeping" entries={t.housekeeping} />
                  <DepartmentRow label="Maintenance" entries={t.maintenance} />
                </Box>
              ))}
            </Stack>

            {/* Legend */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Chip
                size="small"
                label="Dispatched"
                sx={{ bgcolor: "grey.300", color: "text.primary" }}
              />
              <Chip
                size="small"
                color="success"
                label="Completed"
                icon={<CheckCircleIcon />}
              />
            </Box>
          </>
        )}

        {!loading && showToggle && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button size="small" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Show less" : "View history"}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default RoomTurnHistory;
